import React from 'react';

import { useAppSelector, useAppStore } from '@/lib/hooks';

import {
  AnswerData,
  MCAnswerData,
  QuizOption,
  QuizQuestionType,
  ShortAnswerData,
} from 'quiz-types';
import QuizInputText from './inputs/QuizInputText';
import QuizInputTextarea from './inputs/QuizInputTextarea';
import { CheckmarkCircleIcon } from '@/assets/ui';

interface QuizQuestionBuilderProps {
  onClick?: (index: number) => void;
  index: number;
  question: string;
  type: QuizQuestionType;
  options: QuizOption[] | undefined;
}

function QuizQuestionBuilderStatic({
  onClick,
  index,
  question,
  type,
  options,
}: QuizQuestionBuilderProps) {
  const store = useAppStore();
  const answers: Array<AnswerData> = useAppSelector(() => store.getState().quiz.answers);

  const getShortAnswerData = (): string => {
    for (const answer of answers) {
      if (answer.questionIndex === index) {
        const data = answer.answerData as ShortAnswerData;
        return data;
      }
    }
    return '';
  };

  const isChecked = (optionIndex: number): boolean | undefined => {
    for (const answer of answers) {
      if (answer.questionIndex === index) {
        const data = answer.answerData as MCAnswerData;
        return data.includes(optionIndex);
      }
    }
    return false;
  };

  const isUnmarked = (): boolean | undefined => {
    const answer = answers.find((a) => a.questionIndex === index);
    if (answer) {
      return (answer.answerData as MCAnswerData).length < 1;
    }
    return false;
  };

  return (
    <div
      onClick={() => {
        if (typeof onClick === 'function') {
          onClick(index);
        }
      }}
    >
      <div className='py-1 my-2 flex flex-col'>
        {isUnmarked() && (
          <label className='text-[12px] text-red-500 mb-1'>
            Warning: no answer options are explicitly marked as correct for this question. By
            default, this question will be marked correct for all submissions.
          </label>
        )}
        <label className='font-semibold'>{question}</label>
      </div>
      {type === 'Multiple Choice' &&
        options?.map((option, index) => (
          <div key={index} className='flex items-center mb-2'>
            <input type='radio' name={question} className='mr-2' readOnly />
            <span>{option.value}</span>
            <CheckmarkCircleIcon
              className={`${isChecked(index) ? '' : 'hidden'} w-4 h-4 text-success ml-2`}
            />
          </div>
        ))}
      {type === 'Short Answer' && (
        <QuizInputText
          id={`static-short-answer-${index}`}
          label={'Answer'}
          value={getShortAnswerData()}
          onChange={undefined}
          placeholderText={'Short answer'}
          disabled
        />
      )}
      {type === 'Paragraph' && (
        <QuizInputTextarea
          id={`static-paragraph-${index}`}
          label={'Long answer'}
          value={''}
          onChange={undefined}
          placeholderText='Long answer text'
          rows={3}
          disabled
        />
      )}
    </div>
  );
}

export default QuizQuestionBuilderStatic;
